import { useCallback } from 'react';
import { consoleLog } from '@/utils';
import { useGlobalStore } from '@/store';

/**
 * Parameters for the useViewScoreCard hook
 */
interface UseViewScoreCardParams {
  /** The full name of the user viewing the scorecard (used for display in the sidebar) */
  userName: string;
}

/**
 * Return type for the useViewScoreCard hook
 */
interface UseViewScoreCardReturn {
  /** Handler function to trigger the scorecard view with validation and data loading */
  handleViewScoreCardClick: (scoreId: string) => void;
}

/**
 * Custom hook for managing scorecard viewing logic
 *
 * This hook provides a handler function that:
 * 1. Locates a score record by its session ID
 * 2. Finds the corresponding simulation that contains this score in its history
 * 3. Retrieves the associated certification data
 * 4. Validates that all required data exists before proceeding
 * 5. Updates the global store with the complete scorecard data for sidebar display
 *
 * The hook uses `useCallback` to memoize the handler function, preventing unnecessary
 * re-renders in child components that receive this handler as a prop.
 *
 * @param {UseViewScoreCardParams} params - Configuration object
 * @param {string} params.userName - The display name of the user (e.g., "John Doe")
 *
 * @returns {UseViewScoreCardReturn} Object containing the memoized handler function
 *
 * @example
 * ```tsx
 * const { handleViewScoreCardClick } = useViewScoreCard({ userName: "John Doe" });
 *
 * // In a click handler or callback:
 * handleViewScoreCardClick("score-123");
 * ```
 */
export const useViewScoreCard = ({ userName }: UseViewScoreCardParams): UseViewScoreCardReturn => {
  // =====================================================================
  // Store Hooks
  // =====================================================================
  /**
   * Get global store data and setter functions
   * - certifications: List of all user certifications
   * - scores: List of all simulation scores/sessions
   * - simulations: List of all simulations with their history of score IDs
   * - setSidebarScorecard: Function to update the visible scorecard in the sidebar
   */
  const { certifications, scores, setSidebarScorecard, simulations } = useGlobalStore();

  // =====================================================================
  // Callback Handler
  // =====================================================================
  /**
   * Handler function that processes a scorecard view request
   *
   * Flow:
   * 1. Logs the initial request with the score ID
   * 2. Searches for matching score by sessionId
   * 3. Searches for simulation containing this score in its history
   * 4. Searches for certification associated with the score
   * 5. Validates all three data points exist
   * 6. If validation passes: Updates sidebar with complete data
   * 7. If validation fails: Logs warnings and exits early
   *
   * @param {string} scoreId - The unique identifier of the score/session to view
   */
  const handleViewScoreCardClick = useCallback(
    (scoreId: string) => {
      // ================================================================
      // Step 1: Log the request
      // ================================================================
      consoleLog.info(`handleViewScoreCardClick(${scoreId}) called`);

      // ================================================================
      // Step 2-4: Find related data
      // ================================================================
      /**
       * Search for the score record by matching its sessionId with the provided scoreId
       * The sessionId is converted to string for comparison
       */
      const score = scores.find((score) => score.sessionId?.toString() === scoreId);

      /**
       * Search for the simulation that includes this score in its history.
       * Each simulation has a history array containing score/session IDs
       */
      const sim = simulations.find((sims) => sims.history?.some((score) => score === scoreId));

      /**
       * Search for the certification associated with this score.
       * Uses the score's certificationId to find the matching certification record
       * Note: This lookup depends on the score being found first
       */
      const cert = certifications.find((c) => c.id.toString() === score?.certificationId);

      // ================================================================
      // Step 5: Log findings (helpful for debugging)
      // ================================================================
      if (score) {
        consoleLog.info(`handleViewScoreCardClick(${scoreId}) Found score match:`, score);
      }
      if (sim) {
        consoleLog.info(`handleViewScoreCardClick(${scoreId}) Found sim match:`, sim);
      }
      if (cert) {
        consoleLog.info(`handleViewScoreCardClick(${scoreId}) Found cert match:`, cert);
      }

      // ================================================================
      // Step 6: Validate all required data exists
      // ================================================================
      /**
       * Early exit if any required data is missing
       * All three data points (score, simulation, certification) must be present
       */
      if (!score || !sim || !cert) {
        // Log specific warnings for debugging which data is missing
        if (!score) {
          consoleLog.warn(`handleViewScoreCardClick(${scoreId}) Score match not found`);
        }
        if (!sim) {
          consoleLog.warn(`handleViewScoreCardClick(${scoreId}) Sim match not found`);
        }
        if (!cert) {
          consoleLog.warn(`handleViewScoreCardClick(${scoreId}) Cert match not found`);
        }
        return; // Exit early without updating the sidebar
      }

      // ================================================================
      // Step 7: Update global store with complete scorecard data
      // ================================================================
      /**
       * All validation passed. Update the sidebar with the complete scorecard data
       * This will trigger the SlideoutModalSimulationScore component to display
       */
      setSidebarScorecard({
        active: true, // Activate the sidebar/modal
        certification: cert, // The certification details
        score, // The score/session data
        simulation: sim, // The simulation details
        title: null, // Title can be set elsewhere if needed
        userName, // Display name passed from component
      });
    },
    // ================================================================
    // Dependency Array
    // ================================================================
    /**
     * Dependencies for memoization:
     * - certifications: Changes when cert list updates
     * - scores: Changes when score list updates
     * - setSidebarScorecard: Store setter (typically stable)
     * - simulations: Changes when simulation list updates
     * - userName: Passed from parent component props
     */
    [certifications, scores, setSidebarScorecard, simulations, userName],
  );

  // =======================================================================
  // Return the handler function for use in components
  // =======================================================================
  return { handleViewScoreCardClick };
};
